import type { Shadows } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import ExpandMore from "@mui/icons-material/ExpandMore";

const baseShadows = createTheme().shadows;

let theme = createTheme({
  palette: {
    common: {
      black: "#1D1D1D",
      white: "#FFFFFF",
    },
    primary: {
      main: "#0A8276",
      light: "#DFF4F3",
      dark: "#06534B",
    },
    secondary: {
      main: "#BE3283",
      light: "#FF9737",
      dark: "#9C216E",
    },
    error: {
      main: "#CD002F",
    },
    warning: {
      main: "#E16B25",
    },
    info: {
      main: "#0A8276",
    },
    success: {
      main: "#4CA460",
    },
    text: {
      primary: "#1D1D1D",
      secondary: "#575352",
    },
  },
  shadows: [
    "none",
    "0 1px 1px 0 #1D1D1D1F, 0 0 1px 0 #1D1D1D1F", // Shadow 100
    "0 2px 4px 0 #1D1D1D1F, 0 0 1px 0 #1D1D1D1F", // Shadow 200
    "0 4px 8px 0 #1D1D1D1F, 0 0 1px 0 #1D1D1D1F", // Shadow 300
    ...baseShadows.slice(3), // Extend the default shadows
  ] as Shadows,
  shape: {
    borderRadius: 4, // Equivalent to $ifxBorderRadius100
  },
  typography: {
    fontFamily: "Source Sans 3",
    fontSize: 15,
    h1: {
      fontWeight: 600,
      fontSize: "4.25rem",
      lineHeight: "4.75rem",
      fontFamily: "Source Sans 3",
    },
    h2: {
      fontWeight: 600,
      fontSize: "3.5rem",
      lineHeight: "4rem",
      fontFamily: "Source Sans 3",
    },

    // Add other typography variants based on your variables
  },
  spacing: (factor: number) => `${factor * 4}px`, // Base spacing is 8px
  breakpoints: {
    values: {
      xs: 576,
      sm: 768,
      md: 992,
      lg: 1200,
      xl: 1400,
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 0,
          fontWeight: 600,
          fontSize: "1rem",
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-root": {
            borderRadius: 0,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          "height": "40px",
          "borderRadius": 0,
          "& .MuiInputAdornment-root": {
            padding: "0 4px",
            color: "#575352",
          },
        },
        input: {
          "padding": "8px 16px",
          "&::placeholder": {
            color: "#8D8786",
            opacity: 1,
            fontSize: "1.1rem",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: "0 2px 4px 0 #1D1D1D1F, 0 0 1px 0 #1D1D1D1F", // Shadow 200
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "2px",
          borderRadius: 0,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: "0.9rem",
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        IconComponent: ExpandMore,
      },
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: "0 16px 0 0",
          fontSize: "1rem",
        },
        select: {
          paddingRight: 0,
          padding: "8px 16px",
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: "0.9rem",
          color: "#575352",
          textDecoration: "none",
          a: {
            "color": "#1D1D1D",
            "cursor": "pointer",
            "&:hover": {
              color: "#1D1D1D",
            },
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecorationLine: "none",
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          strokeWidth: 0.5,
          stroke: "#ffffff",
        },
      },
    },
  },
});

theme = createTheme(theme, {
  palette: {
    engineering: {
      100: "#F7F7F7",
      200: "#EEEDED",
      300: "#BFBBBB",
      400: "#8D8786",
      500: "#575352",
      600: "#3C3A39",
    },
    ocean: {
      100: "#DFF4F3",
      200: "#B8DEDA",
      300: "#6CB4AD",
      400: "#3B9B91",
      500: "#0A8276",
      600: "#08665C",
      700: "#06534B",
    },
    red: {
      500: "#CD002F",
      600: "#A2001E",
      700: "#900021",
    },
    orange: {
      500: "#E16B25",
    },
    green: {
      500: "#4CA460",
    },
    lawn: {
      400: "#B9D257",
      500: "#9BBA43",
      700: "#3C6C0F",
    },
    berry: {
      400: "#BE3283",
      500: "#9C216E",
    },
    sun: {
      400: "#FF9737",
      500: "#F97414",
    },
    sand: {
      400: "#FBE273",
      500: "#FCD442",
    },
    overlay: {
      light: "#FFFFFF33",
      dark: "#1D1D1D33",
    },
  },
});

export default theme;
